import type { ReactNode } from 'react';
import { Button, Kicker } from './primitives';

/**
 * Fill for me (SG-05): what the platform filled, shown as an inline diff
 * before anything takes it. Each filled line is marked and says where it
 * came from; a line left blank stays visible as the person's to write,
 * rather than disappearing into a form that looks finished. Nothing is
 * taken until the person confirms, and confirming only fills the form in
 * front of them: they still edit it, and they still submit it.
 */
export interface FillLine {
  label: string;
  /** What was filled, or null for a line left for the person. */
  value: string | null;
  /** Where the value came from, in a few words. */
  from?: string | undefined;
}

export function FillForMe({
  title,
  source,
  lines,
  children,
  confirmLabel = 'Use these',
  onConfirm,
  onDismiss,
}: {
  title: string;
  /** What it was filled from, in a sentence. */
  source: string;
  lines: FillLine[];
  /** Controls that change what is filled, such as which records it reads. */
  children?: ReactNode;
  confirmLabel?: string;
  onConfirm?: (() => void) | undefined;
  onDismiss: () => void;
}) {
  const filled = lines.filter((l) => l.value !== null).length;
  return (
    <section className="flex flex-col gap-3 border border-line bg-raised p-4" aria-label={title} data-testid="fill-for-me">
      <Kicker>{title}</Kicker>
      <p className="m-0 max-w-[64ch] text-dense text-secondary">{source}</p>
      {children}
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
        {lines.map((l, i) => (
          <li key={`${l.label}-${i}`} className={`flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-l-2 pl-3 text-dense ${l.value === null ? 'border-line' : 'border-accent'}`}>
            <span className="text-micro uppercase tracking-label text-meta">{l.value === null ? 'Left for you' : 'Filled'}</span>
            <span className="text-ink">{l.label}</span>
            {l.value !== null && <span className="text-secondary">{l.value}</span>}
            {l.from && <span className="text-label text-meta">{l.from}</span>}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        {onConfirm && filled > 0 && (
          <Button variant="primary" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        )}
        <Button onClick={onDismiss}>Leave it</Button>
      </div>
    </section>
  );
}
